import { NgClass } from '@angular/common'
import { Component, computed, inject, input, output } from '@angular/core'
import { DomSanitizer, SafeHtml } from '@angular/platform-browser'
import { EventPowerUpId } from '@app/v2/event/enums/event-power-up-id.enum'

export type PowerUpCard = {
  id: EventPowerUpId
  icon: PowerUpCardIcon
  title: string
  pricing?: string
  description: string
  tags?: string[]
  tagsIcons?: string[]
}

export type SvgIcon = { type: 'svg'; content: string }
export type ImageIcon = { type: 'image'; src: string; alt?: string }
export type PowerUpCardIcon = SvgIcon | ImageIcon

@Component({
  selector: 'app-power-up-card',
  standalone: true,
  imports: [NgClass],
  templateUrl: './power-up-card.component.html',
  styleUrl: './power-up-card.component.scss'
})
export class PowerUpCardComponent {
  public readonly readonly = input(false)
  public readonly card = input.required<PowerUpCard>()
  public readonly cardClicked = output<PowerUpCard>()
  public readonly safeSvg = computed<SafeHtml | null>(() => {
    const icon = this.card().icon
    return this.isSvgIcon(icon) ? this._sanitizer.bypassSecurityTrustHtml(icon.content) : null
  })

  private readonly _sanitizer = inject(DomSanitizer)

  /**
   * Checks if the icon is an SVG icon.
   * @param icon - The icon to check.
   * @returns {boolean} - True if the icon is an SVG icon, false otherwise.
   */
  public isSvgIcon(icon: PowerUpCardIcon): icon is SvgIcon {
    return icon.type === 'svg'
  }

  /**
   * Checks if the icon is an image icon.
   * @param icon - The icon to check.
   * @returns {boolean} - True if the icon is an image icon, false otherwise.
   */
  public isImageIcon(icon: PowerUpCardIcon): icon is ImageIcon {
    return icon.type === 'image'
  }

  /**
   * Handles the click event of the power up card.
   * @param {MouseEvent} domEvent - The click event.
   */
  public onClick(domEvent: MouseEvent): void {
    if (this.readonly()) return
    domEvent.stopPropagation()
    this.cardClicked.emit(this.card())
  }
}
